import type { TransactionReceipt } from 'viem';
import { AbiEventSignatureNotFoundError, decodeEventLog, getEventSelector } from 'viem';

const TRANSFER_ABI = [
  {
    type: 'event',
    name: 'Transfer',
    inputs: [
      { type: 'address', name: 'from', indexed: true },
      { type: 'address', name: 'to', indexed: true },
      { type: 'uint256', name: 'tokenId', indexed: true },
    ],
  },
] as const;
const TRANSFER_EVENT_HASH = getEventSelector(TRANSFER_ABI[0]);

/**
 * @description Returns the list of transfers events and log index, if found,
 * from the transaction receipt
 *
 * @param receipt the receipt of the transaction supposedly containing the Transfer event
 * @returns An array of tranfers, with from, to and tokenId corresponding to each transfer found in the transaction
 */
export function getTransferEventsInfoFromTxReceipt(receipt: TransactionReceipt) {
  const transfers: Record<
    string,
    {
      firstFrom: string;
      lastTo: string;
      tokenId: string;
    }
  > = {};

  for (const log of receipt.logs) {
    if (log.topics[0] !== TRANSFER_EVENT_HASH) {
      continue;
    }

    try {
      const event = decodeEventLog({
        abi: TRANSFER_ABI,
        data: log.data,
        topics: log.topics,
      });
      const tokenId = event.args.tokenId.toString();
      const entry = transfers[tokenId];
      if (!entry) {
        transfers[tokenId] = {
          firstFrom: event.args.from,
          lastTo: event.args.to,
          tokenId,
        };
      } else {
        entry.lastTo = event.args.to;
      }
    } catch (error) {
      if (!(error instanceof AbiEventSignatureNotFoundError)) {
        throw error;
      }
    }
  }
  return Object.values(transfers);
}
